import { type CSSProperties, type ReactNode, useMemo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Icon, Tag } from '@timeedit/ui-components';
import type { TagProps } from '@timeedit/ui-components/antd';

export type SortableTagItem<T = Record<string, unknown>> = {
  id: string;
  label: ReactNode;
  color?: TagProps['color'];
  icon?: ReactNode;
} & T;

type SortableTagProps<T = Record<string, unknown>> = {
  item: SortableTagItem<T>;
  index: number;
  onRemove?: (item: SortableTagItem<T>, index: number) => void;
  removable?: boolean;
  sortable?: boolean;
  renderContent?: (item: SortableTagItem<T>, index: number) => ReactNode;
  className?: string;
  style?: CSSProperties;
  dragHandleAriaLabel?: string;
  removeAriaLabel?: string;
};

const SortableTag = <T = Record<string, unknown>,>({
  item,
  index,
  onRemove,
  removable = true,
  sortable = true,
  renderContent,
  className,
  style: customStyle,
  dragHandleAriaLabel = 'Drag to reorder',
  removeAriaLabel = 'Remove item',
}: SortableTagProps<T>) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    disabled: !sortable,
  });

  const containerStyle = useMemo<CSSProperties>(
    () => ({
      transform: CSS.Translate.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      display: 'inline-flex',
      alignItems: 'center',
    }),
    [transform, transition, isDragging]
  );

  const content = renderContent ? (
    renderContent(item, index)
  ) : (
    <>
      {item.icon}
      {item.label}
    </>
  );

  return (
    <div ref={setNodeRef} style={containerStyle} {...attributes}>
      <Tag
        className={className}
        style={customStyle}
        color={item.color || 'default'}
      >
        {sortable && (
          <Icon
            id="HolderOutlined"
            alt="Holder"
            aria-label={dragHandleAriaLabel}
            style={{ marginRight: 4, cursor: 'grab' }}
            {...listeners}
          />
        )}
        {content}
        {removable && (
          <Icon
            id="CloseOutlined"
            alt="Close"
            aria-label={removeAriaLabel}
            style={{ marginLeft: 4, cursor: 'pointer' }}
            onClick={() => onRemove?.(item, index)}
          />
        )}
      </Tag>
    </div>
  );
};

export default SortableTag;
